import { useMemo } from 'react';
import { User, Clock, TrendingUp, BarChart } from 'lucide-react';
import { PlayerStreamLogEntry } from '../types/player';
import { PlayerProfile } from './PlayerProfile';
import { formatDuration } from '../utils/time';

interface ClusterStatsSummaryProps {
  logs: PlayerStreamLogEntry[];
}

interface ClusterStats {
  totalSessions: number;
  uniquePlayers: number;
  averageDuration: number;
  mostActivePlayer: {
    uuid: string;
    username: string;
    totalPlaytime: number;
  } | null;
  averageBlocksBroken: number;
  averageDistanceTraveled: number;
}

const calculateStats = (logs: PlayerStreamLogEntry[]): ClusterStats => {
  const totalSessions = logs.length;
  if (totalSessions === 0) {
    return {
      totalSessions: 0,
      uniquePlayers: 0,
      averageDuration: 0,
      mostActivePlayer: null,
      averageBlocksBroken: 0,
      averageDistanceTraveled: 0,
    };
  }

  const uniquePlayerUuids = new Set(logs.map(log => log.msg.uuid));
  const playtimeByUser: Record<string, { total: number; username: string }> = {};
  
  let totalPlaytime = 0;
  let totalBlocksBroken = 0;
  let totalDistance = 0;

  for (const log of logs) {
    const { uuid, username, playtime, mc_blocks_broken, mc_distance } = log.msg;
    const sessionPlaytime = parseInt(playtime) || 0;
    
    totalPlaytime += sessionPlaytime;
    totalBlocksBroken += parseInt(mc_blocks_broken) || 0;
    totalDistance += parseInt(mc_distance) || 0;

    if (!playtimeByUser[uuid]) {
      playtimeByUser[uuid] = { total: 0, username };
    }
    playtimeByUser[uuid].total += sessionPlaytime;
  }

  let mostActivePlayer: ClusterStats['mostActivePlayer'] = null;
  let maxPlaytime = 0;
  for (const uuid in playtimeByUser) {
    if (playtimeByUser[uuid].total > maxPlaytime) {
      maxPlaytime = playtimeByUser[uuid].total;
      mostActivePlayer = {
        uuid,
        username: playtimeByUser[uuid].username,
        totalPlaytime: maxPlaytime,
      };
    }
  }

  return {
    totalSessions,
    uniquePlayers: uniquePlayerUuids.size,
    averageDuration: totalPlaytime / totalSessions,
    mostActivePlayer,
    averageBlocksBroken: totalBlocksBroken / totalSessions,
    averageDistanceTraveled: totalDistance / totalSessions,
  };
};

export const ClusterStatsSummary = ({ logs }: ClusterStatsSummaryProps) => {
  const stats = useMemo(() => calculateStats(logs), [logs]);

  return (
    <div className="glass-card p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white">Cluster Session Summary</h2>
        <p className="text-sm text-glass-400">
          (Based on the last {stats.totalSessions.toLocaleString()} sessions)
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
        <StatCard icon={BarChart} label="Total Sessions" value={stats.totalSessions.toLocaleString()} />
        <StatCard icon={User} label="Unique Players" value={stats.uniquePlayers.toLocaleString()} />
        <StatCard icon={Clock} label="Avg. Session" value={formatDuration(Math.round(stats.averageDuration))} />
        <StatCard icon={TrendingUp} label="Avg. Distance" value={`${Math.round(stats.averageDistanceTraveled).toLocaleString()}m`} />
        <StatCard icon={TrendingUp} label="Avg. Blocks Broken" value={Math.round(stats.averageBlocksBroken).toLocaleString()} />
        {stats.mostActivePlayer && (
          <div className="col-span-2 md:col-span-3 lg:col-span-5 flex flex-wrap items-center justify-center gap-3 md:gap-4 glass-panel p-3 md:p-4 rounded-lg">
            <TrendingUp className="w-6 h-6 text-accent-300" />
            <span className="text-glass-300">Most Active Player:</span>
            <PlayerProfile uuid={stats.mostActivePlayer.uuid} name={stats.mostActivePlayer.username} />
            <span className="text-white font-bold">({formatDuration(stats.mostActivePlayer.totalPlaytime)} total)</span>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>, label: string, value: string | number }) => (
  <div className="glass-panel p-4 rounded-lg text-center">
    <Icon className="w-8 h-8 text-accent-400 mx-auto mb-2" />
    <div className="text-xl md:text-2xl font-semibold tracking-tight text-white">{value}</div>
    <div className="text-sm text-glass-400">{label}</div>
  </div>
);
